import { parsePieSocketEvent } from "./liveChatUtils";

/**
 * PieSocket WebSocket client (inbox or room).
 * Connects to the full wss URL returned by ms5 live-chat token/join/connect APIs.
 */
export function createPieSocketClient({ wsUrl, onEvent }) {
	let ws = null;
	let closed = false;
	let reconnectAttempt = 0;
	let reconnectTimer = null;
	const listeners = new Map();

	function emit(event, payload) {
		if (typeof onEvent === "function") onEvent(event, payload);
		const set = listeners.get(event);
		if (set) set.forEach((fn) => fn(payload));
	}

	function scheduleReconnect() {
		if (closed || !wsUrl) return;
		const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttempt));
		reconnectAttempt += 1;
		reconnectTimer = setTimeout(() => {
			reconnectTimer = null;
			connect();
		}, delay);
	}

	function connect() {
		if (closed || !wsUrl) return;
		if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) {
			return;
		}
		try {
			ws = new WebSocket(wsUrl);
		} catch (err) {
			emit("error", err);
			scheduleReconnect();
			return;
		}

		ws.addEventListener("open", () => {
			reconnectAttempt = 0;
			emit("open", null);
		});

		ws.addEventListener("message", (ev) => {
			let raw;
			try {
				raw = JSON.parse(ev.data);
			} catch (_) {
				return;
			}
			const { event, data } = parsePieSocketEvent(raw);
			if (event) emit(event, data);
			emit("raw", raw);
		});

		ws.addEventListener("close", () => {
			emit("close", null);
			ws = null;
			if (!closed) scheduleReconnect();
		});

		ws.addEventListener("error", () => {
			emit("error", new Error("WebSocket error"));
		});
	}

	function on(event, fn) {
		if (!listeners.has(event)) listeners.set(event, new Set());
		listeners.get(event).add(fn);
		return () => listeners.get(event)?.delete(fn);
	}

	function disconnect() {
		closed = true;
		if (reconnectTimer) {
			clearTimeout(reconnectTimer);
			reconnectTimer = null;
		}
		if (ws) {
			try {
				ws.close();
			} catch (_) {}
			ws = null;
		}
	}

	connect();

	return { connect, disconnect, on, isOpen: () => ws?.readyState === WebSocket.OPEN };
}

/** @deprecated use createPieSocketClient */
export function createLiveChatSocket(opts) {
	return createPieSocketClient(opts);
}

/** @deprecated inbox/room URLs come from ms5 API responses */
export function buildLiveChatWsUrl() {
	return "";
}

export function createInboxSocket(opts) {
	return createPieSocketClient(opts);
}

export function createRoomSocket(opts) {
	return createPieSocketClient(opts);
}
